import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodService } from '../../common/commonMethod.service';
import { AccountService } from '../../services/account.service';
import { UserGroupService } from '../../services/userGroup.service';
import { UserService } from '../../services/user.service';
import { RegistrationCode } from '../../models/registrationCode.model';
import { VwSelectOption } from '../../shared/ui/searchable-select/searchable-select.component';

@Component({
  selector: 'app-sigup',
  templateUrl: 'signup.component.html',
  styles: [`
    /* A field that is still wrong after the person tried to submit (or left it) turns red, so every
       missing mandatory field is visible at a glance instead of only the first one. */
    .form-control.ng-invalid.ng-touched { border-color: #dc3545; box-shadow: 0 0 0 .1rem rgba(220, 53, 69, .2); }
    app-searchable-select.ng-invalid.ng-touched ::ng-deep .vw-sel__trigger { border-color: #dc3545; box-shadow: 0 0 0 .1rem rgba(220, 53, 69, .2); }
    .gender-group.is-invalid { outline: 1px solid #dc3545; border-radius: 4px; padding: 6px 0; }
  `],
  providers: [AccountService, CommonMethodService, UserGroupService, UserService]
})
export class SignUpComponent implements OnInit, OnDestroy {

  signupForm: FormGroup;
  codeForm: FormGroup;
  subscribe: Array<Subscription> = [];
  loader: boolean = false;
  step: number = 1;
  registrationCode: RegistrationCode;
  submitted: boolean = false;
  flatList: any[] = [];

  // Country runs to 190+ entries and flatList can be long on a big
  // property — both were the actual "scroll a plain <select>" complaint.
  get countryOptions(): VwSelectOption[] {
    return this.countryList.map(c => ({ value: c.countryName, label: c.countryName }));
  }

  get flatOptions(): VwSelectOption[] {
    return this.flatList.map(f => ({
      value: String(f.id),
      label: `${f.name}${f.parentName ? ' [' + f.parentName + ']' : ''}${f.superParentName ? ' [' + f.superParentName + ']' : ''}`
    }));
  }
  assignedGroupName: string = '';
  countryList: any[] = [];

  constructor(private route: Router, private acticeRoute: ActivatedRoute,
    private toaster: ToastrService, private commonMethod: CommonMethodService, private accountService: AccountService,
    private userGroupService: UserGroupService, private userService: UserService) {}

  ngOnInit(): void {
    this.codeForm = new FormGroup({
      code: new FormControl('', [Validators.required]),
    });

    this.signupForm = new FormGroup({
      fullName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(50)]),
      gender: new FormControl('', [Validators.required]),
      username: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      password: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      confirmPassword: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      contactNo: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{8,14}$/)]),
      dialingCode: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.maxLength(150)]),
      country: new FormControl('', [Validators.required]),
      county: new FormControl('', [Validators.maxLength(50)]),
      city: new FormControl('', [Validators.maxLength(50)]),
      premiseHierarchyDetailsId: new FormControl(''),
      employeeId: new FormControl('', [Validators.maxLength(50)]),
      documentType: new FormControl(''),
      documentId: new FormControl('', [Validators.maxLength(100)]),
      profile: new FormControl('', [Validators.maxLength(100)]),
      nationality: new FormControl(''),
      timeIn: new FormControl('', [Validators.maxLength(20)]),
      timeOut: new FormControl('', [Validators.maxLength(20)]),
      residentType: new FormControl(''),
      userGroupId: new FormControl(''),
      // Resident-only fields (the template already shows them for a resident admin).
      relationship: new FormControl('', [Validators.maxLength(50)]),
      secContactNo: new FormControl('', [Validators.pattern(/^[0-9]{8,14}$/)]),
      secDialingCode: new FormControl(''),
      dob: new FormControl(''),
    });
  }

  verifyCode(): void {
    if (this.codeForm.invalid) {
      this.commonMethod.checkFormValidOrNot(this.codeForm);
      return;
    }

    this.loader = true;
    const code = this.codeForm.value.code;
    this.subscribe.push(this.accountService.validateRegistrationCode(code).subscribe(
      (data: RegistrationCode) => {
        this.loader = false;
        this.registrationCode = data;
        this.step = 2;
        this.toaster.success('Code verified. Please fill in your details.');
        
        const premiseControl = this.signupForm.get('premiseHierarchyDetailsId');
        const residentTypeControl = this.signupForm.get('residentType');
        
        if (this.isDeviceRole()) {
          premiseControl.clearValidators();
          premiseControl.setValue('');
          premiseControl.disable();
          residentTypeControl.clearValidators();
          residentTypeControl.setValue('');
          residentTypeControl.disable();
        } else if (this.isResidentRole()) {
          this.signupForm.get('documentType').setValidators([Validators.required]);
          this.signupForm.get('documentId').setValidators([Validators.required, Validators.maxLength(100)]);
          this.signupForm.get('documentType').updateValueAndValidity();
          this.signupForm.get('documentId').updateValueAndValidity();
          premiseControl.setValidators([Validators.required]);
          premiseControl.enable();
          residentTypeControl.setValidators([Validators.required]);
          residentTypeControl.enable();
        } else {
          premiseControl.setValidators([Validators.required]);
          premiseControl.enable();
          residentTypeControl.clearValidators();
          residentTypeControl.setValue('');
          residentTypeControl.disable();
        }
        
        premiseControl.updateValueAndValidity();
        residentTypeControl.updateValueAndValidity();
        
        if (data.userGroupId) {
          this.signupForm.get('userGroupId').setValue(data.userGroupId);
          this.userGroupService.findUserGroupById(data.userGroupId).subscribe(
            (groupData: any) => {
              this.assignedGroupName = groupData.groupName || 'Group ' + data.userGroupId;
            },
            error => {
              this.assignedGroupName = 'Group ' + data.userGroupId;
            }
          );
        }
        
        if (data.accountId) {
          this.loadPremiseOptions(data.accountId);
          this.loadCountryList();
        }
      }, error => {
        this.loader = false;
        this.commonMethod.returnErrorMessage(error);
      }));
  }

  loadPremiseOptions(accountId: number): void {
    this.subscribe.push(this.accountService.getAllPremiseLevel(accountId).subscribe(
      (levels: any[]) => {
        if (!levels || levels.length === 0) {
          this.flatList = [];
          return;
        }
        
        let maxLevel = 0;
        let maxLevelId: number;
        levels.forEach(level => {
          if (level.level > maxLevel) {
            maxLevel = level.level;
            maxLevelId = level.id;
          }
        });
        
        if (maxLevelId) {
          this.subscribe.push(this.accountService.getUpperLevelList(accountId, maxLevelId).subscribe(
            (upperData: any[]) => {
              if (upperData && upperData.length > 0) {
                this.flatList = upperData;
              } else {
                this.accountService.getAllFlatList(accountId).subscribe(
                  (flatData: any[]) => {
                    this.flatList = flatData || [];
                  },
                  error => {
                    console.error('Failed to load flat list', error);
                    this.flatList = [];
                  }
                );
              }
            },
            error => {
              console.error('Failed to load upper level list', error);
              this.flatList = [];
            }
          ));
        }
      },
      error => {
        console.error('Failed to load premise levels', error);
        this.flatList = [];
      }
    ));
  }

  loadCountryList(): void {
    this.subscribe.push(this.userService.getCountryList('').subscribe(
      (data: any[]) => {
        this.countryList = data || [];
      },
      error => {
        console.error('Failed to load country list', error);
        this.countryList = [];
      }
    ));
  }

  onCountryChange(): void {
    const selectedCountry = this.countryList.find(c => c.countryName === this.signupForm.get('country').value);
    if (selectedCountry) {
      const secondary = this.signupForm.get('secDialingCode');
      // The secondary number usually shares the primary's country: preselect it unless the person already chose another.
      if (!secondary.value || secondary.value === this.signupForm.get('dialingCode').value) {
        secondary.setValue(selectedCountry.dialingCode);
      }
      this.signupForm.get('dialingCode').setValue(selectedCountry.dialingCode);
      this.signupForm.get('nationality').setValue(selectedCountry.nationality);
    } else {
      this.signupForm.get('dialingCode').setValue('');
      this.signupForm.get('nationality').setValue('');
    }
  }

  // True when the field was touched and fails the given validator — keeps the template messages short.
  hasError(field: string, error: string): boolean {
    const control = this.signupForm.get(field);
    return !!control && control.touched && !!control.errors && !!control.errors[error];
  }

  // Shift Time In/Out dropdown time-pickers — each field keeps its own
  // open state, draft Date and 12h/24h display mode so switching one
  // field's format doesn't affect the other. The form control itself still
  // just holds a plain "hh:mm AM/PM" (or "HH:mm") string.
  timeInOpen = false;
  timeOutOpen = false;
  timeInDate: Date = new Date(0, 0, 0, 8, 0);
  timeOutDate: Date = new Date(0, 0, 0, 17, 0);
  timeInMeridian = true;
  timeOutMeridian = true;

  toggleTimeIn(): void {
    this.timeInOpen = !this.timeInOpen;
  }

  toggleTimeOut(): void {
    this.timeOutOpen = !this.timeOutOpen;
  }

  applyTimeIn(): void {
    this.signupForm.get('timeIn').setValue(this.formatTime(this.timeInDate, this.timeInMeridian));
    this.signupForm.get('timeIn').markAsTouched();
    this.timeInOpen = false;
  }

  applyTimeOut(): void {
    this.signupForm.get('timeOut').setValue(this.formatTime(this.timeOutDate, this.timeOutMeridian));
    this.signupForm.get('timeOut').markAsTouched();
    this.timeOutOpen = false;
  }

  clearTimeIn(): void {
    this.signupForm.get('timeIn').setValue('');
    this.timeInOpen = false;
  }

  clearTimeOut(): void {
    this.signupForm.get('timeOut').setValue('');
    this.timeOutOpen = false;
  }

  private formatTime(date: Date, meridian: boolean): string {
    if (!date) {
      return '';
    }
    const hours24 = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    if (!meridian) {
      return `${hours24.toString().padStart(2, '0')}:${minutes}`;
    }
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12.toString().padStart(2, '0')}:${minutes} ${period}`;
  }

  get genderInvalid(): boolean {
    const gender = this.signupForm.get('gender');
    return gender.touched && gender.invalid;
  }

  // A secondary number needs its dialing code (and the other way round makes no sense without a number).
  get secondaryCodeMissing(): boolean {
    const f = this.signupForm.controls;
    return !!f.secContactNo.value && !f.secDialingCode.value;
  }

  get passwordsMismatch(): boolean {
    const f = this.signupForm.controls;
    return f.confirmPassword.touched && !!f.confirmPassword.value && f.password.value !== f.confirmPassword.value;
  }

  onSubmit(): void {
    if (this.signupForm.invalid) {
      this.commonMethod.checkFormValidOrNot(this.signupForm);
      return;
    }

    if (this.secondaryCodeMissing) {
      this.signupForm.get('secDialingCode').markAsTouched();
      this.toaster.error('Please select the secondary dialing code');
      return;
    }

    const details = this.signupForm.value;
    if (details.fullName === null || details.fullName === undefined || details.fullName.trim() === '') {
      this.toaster.error('Please enter full name');
      return;
    }
    if (details.username === null || details.username === undefined || details.username.trim() === '') {
      this.toaster.error('Please enter username');
      return;
    }
    if (details.password !== details.confirmPassword) {
      this.toaster.error('Password and confirm password must be same');
      return;
    }
    if (details.email === null || details.email === undefined || details.email.trim() === '') {
      this.toaster.error('Please enter email');
      return;
    }
    if (details.contactNo === null || details.contactNo === undefined || details.contactNo.trim() === '') {
      this.toaster.error('Please enter contact number');
      return;
    }
    if (details.dialingCode === null || details.dialingCode === undefined || details.dialingCode.trim() === '') {
      this.toaster.error('Please enter dialing code');
      return;
    }

    this.loader = true;
    const payload = {
      code: this.registrationCode.code,
      ...details
    };

    this.subscribe.push(this.accountService.submitRegistrationRequest(payload).subscribe(
      data => {
        this.loader = false;
        this.submitted = true;
        this.step = 3;
      }, error => {
        this.loader = false;
        this.commonMethod.returnErrorMessage(error);
      }));
  }

  getRoleDisplayName(): string {
    if (!this.registrationCode) return '';
    switch (this.registrationCode.roleType.toUpperCase()) {
      case 'EMPLOYEE':
        return 'Staff/Employee';
      case 'RESIDENT':
        return 'Resident Admin';
      case 'DEVICE':
        return 'Security';
      default:
        return this.registrationCode.roleType;
    }
  }

  isEmployeeRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'EMPLOYEE';
  }

  isResidentRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'RESIDENT';
  }

  isDeviceRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'DEVICE';
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(destroyExistingValue => {
      destroyExistingValue.unsubscribe();
    });
  }
}
